/**
 * Life4Earth V2 — i18n Configuration
 *
 * Initializes i18next with:
 *   - English locale bundles (namespace-isolated)
 *   - Browser language detection (localStorage → navigator → html)
 *   - react-i18next integration
 *
 * Convention: Every user-facing string is wrapped in t('namespace:key').
 * Default namespace is 'common'.
 *
 * To add a language:
 *   1. Create src/i18n/locales/<lang>/*.json  (copy en/ as template)
 *   2. Import and add to `resources` below.
 *   3. Run `npx i18next-parser` to extract new t() keys.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/* ---- English locale bundles (one per namespace) ---- */
import enCommon from './locales/en/common.json';
import enLeftNav from './locales/en/leftNav.json';
import enUserProfile from './locales/en/userProfile.json';
import enTheme from './locales/en/theme.json';
import enDiscover from './locales/en/discover.json';
import enProjectConfig from './locales/en/projectConfig.json';
import enCommunity from './locales/en/community.json';
import enGeography from './locales/en/geography.json';
import enKpi from './locales/en/kpi.json';
import enPages from './locales/en/pages.json';
import enChat from './locales/en/chat.json';
import enLanding from './locales/en/landing.json';
import enCopilot from './locales/en/copilot.json';
import enProjectTeam from './locales/en/projectTeam.json';
import enContent from './locales/en/content.json';

/* ---- Arabic locale bundles ---- */
import arCommon from './locales/ar/common.json';
import arLeftNav from './locales/ar/leftNav.json';
import arUserProfile from './locales/ar/userProfile.json';
import arTheme from './locales/ar/theme.json';
import arDiscover from './locales/ar/discover.json';
import arProjectConfig from './locales/ar/projectConfig.json';
import arCommunity from './locales/ar/community.json';
import arGeography from './locales/ar/geography.json';
import arKpi from './locales/ar/kpi.json';
import arPages from './locales/ar/pages.json';
import arChat from './locales/ar/chat.json';
import arLanding from './locales/ar/landing.json';
import arCopilot from './locales/ar/copilot.json';
import arProjectTeam from './locales/ar/projectTeam.json';
import arContent from './locales/ar/content.json';

/* ---- French locale bundles ---- */
import frCommon from './locales/fr/common.json';
import frLeftNav from './locales/fr/leftNav.json';
import frUserProfile from './locales/fr/userProfile.json';
import frTheme from './locales/fr/theme.json';
import frDiscover from './locales/fr/discover.json';
import frProjectConfig from './locales/fr/projectConfig.json';
import frCommunity from './locales/fr/community.json';
import frGeography from './locales/fr/geography.json';
import frKpi from './locales/fr/kpi.json';
import frPages from './locales/fr/pages.json';
import frChat from './locales/fr/chat.json';
import frLanding from './locales/fr/landing.json';
import frCopilot from './locales/fr/copilot.json';
import frProjectTeam from './locales/fr/projectTeam.json';
import frContent from './locales/fr/content.json';

/* ---- German locale bundles ---- */
import deCommon from './locales/de/common.json';
import deLeftNav from './locales/de/leftNav.json';
import deUserProfile from './locales/de/userProfile.json';
import deTheme from './locales/de/theme.json';
import deDiscover from './locales/de/discover.json';
import deProjectConfig from './locales/de/projectConfig.json';
import deCommunity from './locales/de/community.json';
import deGeography from './locales/de/geography.json';
import deKpi from './locales/de/kpi.json';
import dePages from './locales/de/pages.json';
import deChat from './locales/de/chat.json';
import deLanding from './locales/de/landing.json';
import deCopilot from './locales/de/copilot.json';
import deProjectTeam from './locales/de/projectTeam.json';
import deContent from './locales/de/content.json';

/* ---- Ukrainian locale bundles ---- */
import ukCommon from './locales/uk/common.json';
import ukLeftNav from './locales/uk/leftNav.json';
import ukUserProfile from './locales/uk/userProfile.json';
import ukTheme from './locales/uk/theme.json';
import ukDiscover from './locales/uk/discover.json';
import ukProjectConfig from './locales/uk/projectConfig.json';
import ukCommunity from './locales/uk/community.json';
import ukGeography from './locales/uk/geography.json';
import ukKpi from './locales/uk/kpi.json';
import ukPages from './locales/uk/pages.json';
import ukChat from './locales/uk/chat.json';
import ukLanding from './locales/uk/landing.json';
import ukCopilot from './locales/uk/copilot.json';
import ukProjectTeam from './locales/uk/projectTeam.json';
import ukContent from './locales/uk/content.json';

/* ---- Swedish locale bundles ---- */
import svCommon from './locales/sv/common.json';
import svLeftNav from './locales/sv/leftNav.json';
import svUserProfile from './locales/sv/userProfile.json';
import svTheme from './locales/sv/theme.json';
import svDiscover from './locales/sv/discover.json';
import svProjectConfig from './locales/sv/projectConfig.json';
import svCommunity from './locales/sv/community.json';
import svGeography from './locales/sv/geography.json';
import svKpi from './locales/sv/kpi.json';
import svPages from './locales/sv/pages.json';
import svChat from './locales/sv/chat.json';
import svLanding from './locales/sv/landing.json';
import svCopilot from './locales/sv/copilot.json';
import svProjectTeam from './locales/sv/projectTeam.json';
import svContent from './locales/sv/content.json';

/* ---- Russian locale bundles ---- */
import ruCommon from './locales/ru/common.json';
import ruLeftNav from './locales/ru/leftNav.json';
import ruUserProfile from './locales/ru/userProfile.json';
import ruTheme from './locales/ru/theme.json';
import ruDiscover from './locales/ru/discover.json';
import ruProjectConfig from './locales/ru/projectConfig.json';
import ruCommunity from './locales/ru/community.json';
import ruGeography from './locales/ru/geography.json';
import ruKpi from './locales/ru/kpi.json';
import ruPages from './locales/ru/pages.json';
import ruChat from './locales/ru/chat.json';
import ruLanding from './locales/ru/landing.json';
import ruCopilot from './locales/ru/copilot.json';
import ruProjectTeam from './locales/ru/projectTeam.json';
import ruContent from './locales/ru/content.json';

/* ---- Spanish locale bundles ---- */
import esCommon from './locales/es/common.json';
import esLeftNav from './locales/es/leftNav.json';
import esUserProfile from './locales/es/userProfile.json';
import esTheme from './locales/es/theme.json';
import esDiscover from './locales/es/discover.json';
import esProjectConfig from './locales/es/projectConfig.json';
import esCommunity from './locales/es/community.json';
import esGeography from './locales/es/geography.json';
import esKpi from './locales/es/kpi.json';
import esPages from './locales/es/pages.json';
import esChat from './locales/es/chat.json';
import esLanding from './locales/es/landing.json';
import esCopilot from './locales/es/copilot.json';
import esProjectTeam from './locales/es/projectTeam.json';
import esContent from './locales/es/content.json';

const resources = {
  en: {
    common:        enCommon,
    leftNav:       enLeftNav,
    userProfile:   enUserProfile,
    theme:         enTheme,
    discover:      enDiscover,
    projectConfig: enProjectConfig,
    community:     enCommunity,
    geography:     enGeography,
    kpi:           enKpi,
    pages:         enPages,
    chat:          enChat,
    landing:       enLanding,
    copilot:       enCopilot,
    projectTeam:   enProjectTeam,
    content:       enContent,
  },
  ar: {
    common:        arCommon,
    leftNav:       arLeftNav,
    userProfile:   arUserProfile,
    theme:         arTheme,
    discover:      arDiscover,
    projectConfig: arProjectConfig,
    community:     arCommunity,
    geography:     arGeography,
    kpi:           arKpi,
    pages:         arPages,
    chat:          arChat,
    landing:       arLanding,
    copilot:       arCopilot,
    projectTeam:   arProjectTeam,
    content:       arContent,
  },
  fr: {
    common:        frCommon,
    leftNav:       frLeftNav,
    userProfile:   frUserProfile,
    theme:         frTheme,
    discover:      frDiscover,
    projectConfig: frProjectConfig,
    community:     frCommunity,
    geography:     frGeography,
    kpi:           frKpi,
    pages:         frPages,
    chat:          frChat,
    landing:       frLanding,
    copilot:       frCopilot,
    projectTeam:   frProjectTeam,
    content:       frContent,
  },
  de: {
    common:        deCommon,
    leftNav:       deLeftNav,
    userProfile:   deUserProfile,
    theme:         deTheme,
    discover:      deDiscover,
    projectConfig: deProjectConfig,
    community:     deCommunity,
    geography:     deGeography,
    kpi:           deKpi,
    pages:         dePages,
    chat:          deChat,
    landing:       deLanding,
    copilot:       deCopilot,
    projectTeam:   deProjectTeam,
    content:       deContent,
  },
  uk: {
    common:        ukCommon,
    leftNav:       ukLeftNav,
    userProfile:   ukUserProfile,
    theme:         ukTheme,
    discover:      ukDiscover,
    projectConfig: ukProjectConfig,
    community:     ukCommunity,
    geography:     ukGeography,
    kpi:           ukKpi,
    pages:         ukPages,
    chat:          ukChat,
    landing:       ukLanding,
    copilot:       ukCopilot,
    projectTeam:   ukProjectTeam,
    content:       ukContent,
  },
  sv: {
    common:        svCommon,
    leftNav:       svLeftNav,
    userProfile:   svUserProfile,
    theme:         svTheme,
    discover:      svDiscover,
    projectConfig: svProjectConfig,
    community:     svCommunity,
    geography:     svGeography,
    kpi:           svKpi,
    pages:         svPages,
    chat:          svChat,
    landing:       svLanding,
    copilot:       svCopilot,
    projectTeam:   svProjectTeam,
    content:       svContent,
  },
  ru: {
    common:        ruCommon,
    leftNav:       ruLeftNav,
    userProfile:   ruUserProfile,
    theme:         ruTheme,
    discover:      ruDiscover,
    projectConfig: ruProjectConfig,
    community:     ruCommunity,
    geography:     ruGeography,
    kpi:           ruKpi,
    pages:         ruPages,
    chat:          ruChat,
    landing:       ruLanding,
    copilot:       ruCopilot,
    projectTeam:   ruProjectTeam,
    content:       ruContent,
  },
  es: {
    common:        esCommon,
    leftNav:       esLeftNav,
    userProfile:   esUserProfile,
    theme:         esTheme,
    discover:      esDiscover,
    projectConfig: esProjectConfig,
    community:     esCommunity,
    geography:     esGeography,
    kpi:           esKpi,
    pages:         esPages,
    chat:          esChat,
    landing:       esLanding,
    copilot:       esCopilot,
    projectTeam:   esProjectTeam,
    content:       esContent,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: ['common', 'leftNav', 'userProfile', 'theme', 'discover', 'projectConfig', 'community', 'geography', 'kpi', 'pages', 'chat', 'landing', 'copilot', 'projectTeam', 'content'],
    interpolation: {
      escapeValue: false, // React already escapes JSX
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'l4e_language',
    },
  });

export default i18n;
